import React, { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  Checkbox,
  IconButton,
  Tooltip,
} from "@mui/material";
import EditIcon from "../../../assets/icons/edit.svg";
import DeleteIcon from "../../../assets/icons/delete-red.svg";
import "./modal-table.scss";
import { formatDate } from "../../../utils/utils";

interface CampaignOwner {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
}

interface Campaign {
  id: number;
  name: string;
  type: string;
  status: string;
  start_date: string;
  end_date: string;
  campaignOwner?: CampaignOwner;
  expected_revenue?: string;
  budgeted_cost?: string;
  membership_status?: string;
  campaign_lead_association_id?: any;
}

interface CampaignTableProps {
  data: Campaign[];
  selectedCampaign?: { id: number; name: string } | null;
  setSelectedCampaign?: (campaign: { id: number; name: string }) => void;
  multiple?: boolean;
  selectedCampaigns?: { id: number; name: string }[];
  setSelectedCampaigns?: (campaigns: { id: number; name: string }[]) => void;
  viewCampaign?: boolean;
  onDelete?: (campaignId: number, campaignLeadAssociationId: number) => void;
  onEditStatus?: (campaign: Campaign) => void;
  noDataMessage?: string;
  isCustomer?: boolean;
  isTaskActionDisabled?: boolean;
}

const CampaignTable: React.FC<CampaignTableProps> = ({
  data,
  selectedCampaign,
  setSelectedCampaign,
  selectedCampaigns,
  setSelectedCampaigns,
  multiple,
  viewCampaign = false,
  onDelete,
  onEditStatus,
  noDataMessage,
  isCustomer = false,
  isTaskActionDisabled
}) => {
  return (
    <>
      <TableContainer className="campaign-modal__table-container">
        <Table
          className={`campaigns-table ${!data.length ? "no-table-border" : ""}`}
        >
          <TableHead>
            <TableRow>
              <TableCell />
              <TableCell>Campaign Name</TableCell>
              {viewCampaign ? (
                <>
                  <TableCell>Status</TableCell>
                  <TableCell>Type</TableCell>
                </>
              ) : (
                <>
                  <TableCell>Type</TableCell>
                  <TableCell>Status</TableCell>
                </>
              )}
              <TableCell>Start Date</TableCell>
              <TableCell>End Date</TableCell>
              {viewCampaign ? (
                <>
                  <TableCell>Expected Revenue</TableCell>
                  <TableCell>Budgeted Cost</TableCell>
                  <TableCell>Member Status</TableCell>
                </>
              ) : !isCustomer ? (
                <TableCell>Campaign Owner</TableCell>
              ) : (
                <TableCell>Member Status</TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {data && data.length > 0 ? (
              (data || []).map((campaign, index) => (
                <TableRow key={campaign.id}>
                  {!isCustomer ? (
                    <TableCell>
                      {viewCampaign ? (
                        <div style={{ display: "flex", padding: "0px" }}>
                          <Tooltip title="Edit">
                            <IconButton
                              className="icon-button"
                              onClick={() => onEditStatus?.(campaign)}
                              disabled={isTaskActionDisabled}
                            >
                              <img
                                src={EditIcon}
                                alt="edit"
                                className="img-icon"
                              />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Unassign">
                            <IconButton
                              className="icon-button"
                              onClick={() =>
                                onDelete?.(
                                  campaign.id,
                                  campaign.campaign_lead_association_id
                                )
                              }
                              disabled={isTaskActionDisabled}
                            >
                              <img
                                src={DeleteIcon}
                                alt="delete"
                                className="img-icon"
                              />
                            </IconButton>
                          </Tooltip>
                        </div>
                      ) : (
                        <FormControl>
                          {multiple ? (
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={selectedCampaigns?.some(
                                    (c) => c.id === campaign.id
                                  )}
                                  onChange={(e: any) => {
                                    if (e.target.checked) {
                                      setSelectedCampaigns?.([
                                        ...(selectedCampaigns || []),
                                        {
                                          id: campaign.id,
                                          name: campaign.name,
                                        },
                                      ]);
                                    } else {
                                      setSelectedCampaigns?.(
                                        (selectedCampaigns || []).filter(
                                          (c) => c.id !== campaign.id
                                        )
                                      );
                                    }
                                  }}
                                />
                              }
                              label=""
                            />
                          ) : !isCustomer ? (
                            <RadioGroup
                              name="campaign-selection"
                              value={selectedCampaign?.id?.toString() || ""}
                              onChange={() =>
                                setSelectedCampaign?.({
                                  id: campaign.id,
                                  name: campaign.name,
                                })
                              }
                            >
                              <FormControlLabel
                                value={campaign.id.toString()}
                                control={<Radio />}
                                label=""
                              />
                            </RadioGroup>
                          ) : null}
                        </FormControl>
                      )}
                    </TableCell>
                  ):<TableCell style={{borderRight: "none", width: "20px"}} />}

                  <TableCell>{campaign.name}</TableCell>
                  {viewCampaign ? (
                    <>
                      <TableCell>{campaign.status}</TableCell>
                      <TableCell>{campaign.type}</TableCell>
                    </>
                  ) : (
                    <>
                      <TableCell>{campaign.type}</TableCell>
                      <TableCell>{campaign.status}</TableCell>
                    </>
                  )}
                  <TableCell>{formatDate(campaign.start_date)}</TableCell>
                  <TableCell>{formatDate(campaign.end_date)}</TableCell>
                  {viewCampaign ? (
                    <>
                      <TableCell>
                        {campaign.expected_revenue
                          ? `$ ${campaign.expected_revenue}`
                          : "-"}
                      </TableCell>
                      <TableCell>
                        {campaign.budgeted_cost
                          ? `$ ${campaign.budgeted_cost}`
                          : "-"}
                      </TableCell>
                      <TableCell>{campaign.membership_status || "-"}</TableCell>
                    </>
                  ) : !isCustomer ? (
                    <TableCell>
                      {campaign.campaignOwner?.first_name}{" "}
                      {campaign.campaignOwner?.last_name}
                    </TableCell>
                  ) : (
                    <TableCell>{campaign.membership_status || "-"}</TableCell>
                  )}
                </TableRow>
              ))
            ) : (
              <TableRow className="no-border-row">
                <TableCell colSpan={viewCampaign ? 10 : 7} align="center">
                  {noDataMessage || "No campaigns found."}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};

export default CampaignTable;
